import type { LabeebTierRow, LabeebTiers } from "./labeeb";

/**
 * A limit key rendered for a human — `DailyMessageLimit` becomes "Daily
 * message limit" — rather than hard-coding a label per key the reference doc
 * happens to name today. A renamed or added limit still reads as words
 * instead of falling back to the raw key or, worse, being dropped silently.
 */
export function limitLabel(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * The console URL for one Labeeb tier.
 *
 * Beside `limitValue` rather than in `api/labeeb.ts`: that file holds the
 * product's API paths, and this is a route in this app — `labeebTierPath` is
 * the PATCH endpoint and the two must not be mistaken for each other.
 */
export function labeebTierHref(tierId: string): string {
  return `/policy/tiers/${encodeURIComponent(tierId)}`;
}

/**
 * One of a tier's two limits, by key.
 *
 * The envelope used to be an open `limits: Record<string, number>` whose keys
 * came from the response, so a renamed limit shrank the table instead of
 * leaving dashes. The new shape has exactly two named integers, so the keys
 * are fixed and this reads them by name.
 *
 * `-1` is unlimited and a missing number is a dash — never a zero, which
 * would read as "no messages allowed".
 */
export function limitValue(row: LabeebTierRow, key: string): string {
  const v = key === "daily" ? row.daily : key === "monthly" ? row.monthly : undefined;
  if (v === undefined || v === null) return "—";
  if (v === -1) return "Unlimited";
  return String(v);
}

/** The two limits every Labeeb tier carries, in render order. */
export const LABEEB_LIMIT_KEYS = ["daily", "monthly"] as const;

/**
 * Which attributes a tier write may name.
 *
 * `editable_attributes` from the response when it is there, falling back to
 * the columns actually present. `Features` is deliberately NOT editable —
 * nothing in the runtime reads it, so offering it would imply a gate that
 * does not exist — and the server's own list is the authority on that.
 */
export function editableAttributesOf(
  _data: LabeebTiers | undefined | null,
  _fallback: string[],
): string[] {
  // `editable_attributes` is gone from the response: `PATCH /admin/tiers/{id}`
  // takes `daily` and `monthly` and nothing else, so the editable set is fixed
  // by the API rather than advertised per response. Keeping the function
  // signature means its two call sites did not have to change shape.
  return [...LABEEB_LIMIT_KEYS];
}
